// @flow
import * as React from 'react';
import { RequiredActionProps } from '../../types';
import { Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import moment from 'moment/moment';
import { ArrowSquareOutIcon, XIcon } from '@phosphor-icons/react';
import {
  Portal, useAgent, useAppointmentReminderSuggestedToRescheduleAppointmentConfirmByAgentMutation,
   useInterval, UserAvatar
} from '../../../../shared';
import { useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { ScheduleACallButton } from '@coverright/features';
import { environment } from '../../../../../environments/environment';
import { getIsCSR, keycloak, KeycloakRoles } from '@coverright/shared/keycloak';


export const ArAgentRescheduleRequiredAction = ({item, onResolved, onHide}: RequiredActionProps) => {
  const details = useMemo(() => JSON.parse(item.detailsJson), [item.detailsJson]);
  const [done] = useAppointmentReminderSuggestedToRescheduleAppointmentConfirmByAgentMutation({
    variables: {id: item.id},
  })

  const complete = async () => {
    await done();
    onResolved();
  }

  return (
    <Portal sx={{'&:hover': {border: '1px solid #d0d5e1' }, transition: 'all .3s', mb: 2, background: '#FFF', minWidth: 366 }}>
      <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'space-between'}
             sx={{background: 'linear-gradient(137deg, rgba(247, 231, 186, 1) 0%, rgb(231 253 255) 70%)'}} p={'8px 12px'}>
        <Typography flex={1} variant={'h5'} fontWeight={500} noWrap>
          You have to reschedule your appointment
        </Typography>

        <Typography variant={'caption'} align={'right'}>
          {moment(item.createdAt).format('L H:mm')}
        </Typography>

        {onHide && <ButtonBase onClick={onHide}>
          <XIcon size={16} color={'#1c434f'} weight={'bold'} />
        </ButtonBase>}
      </Stack>

      <Typography flex={1} p={1} variant={'body2'}>
        Appointment was scheduled for: <b>{moment(details.startTime).format('L H:mm')}</b>
      </Typography>

      <Box>
        <ButtonBase onClick={() => window.open(`/${'client'}/${details.invitee?.clientId}`, '_blank')}
                    sx={{
                      padding: '4px 8px',
                      borderRadius: '8px'
                    }}
        >
          <UserAvatar size={32}
                      fontSize={13}
                      showEmail
                      email={details.invitee?.email}
                      name={details.invitee?.name} />
        </ButtonBase>
      </Box>

      <Stack p={1} justifyContent={'flex-end'} gap={1} direction={'row'}>
        <NewMeetingButton id={details.invitee?.uri.split('/').pop() || ''}
                          startDate={details.startTime}
                          client={{
                            firstName: details.invitee?.name,
                            email: details.invitee?.email,
                            phoneNumber: details.invitee?.phoneNumber,
                          }}
                          onEventScheduled={complete} />
        <LoadingButton sx={{width: 100}} size={'small'} variant={'contained'} onClick={complete}>
          Done
        </LoadingButton>
      </Stack>
    </Portal>
  );
};

type NewMeetingButtonProps = {
  id: string,
  startDate: string,
  onEventScheduled: () => void,
  client?: {
    firstName?: string | null,
    lastName?: string | null,
    email?: string | null,
    phoneNumber?: string | null,
  }
}

const NewMeetingButton = ({id, startDate, onEventScheduled, client}: NewMeetingButtonProps) => {
  const [isBeforeNow, setIsBeforeNow] = useState(isBefore(startDate));
  const agent = useAgent();
  const isCSR = getIsCSR(keycloak)

  useInterval(() => setIsBeforeNow(isBefore(startDate)), 5000);

  if (isBeforeNow) {
    return <ScheduleACallButton
      variant={'outlined'}
      size={'small'}
      sx={{width: 180}}
      endIcon={<ArrowSquareOutIcon />}
      fullWidth
      calendlyProps={{
        dataUrl: environment.production && isCSR ? 'https://calendly.com/coverright-advisors/planning' : undefined,
        agent,
        client,
        onEventScheduled
      }}>
      Schedule new call
    </ScheduleACallButton>
  }

  return <Button sx={{width: 150}} size={'small'}
                 onClick={() => window.open(`https://calendly.com/reschedulings/${id}`)}
                 endIcon={<ArrowSquareOutIcon />}
                 variant={'outlined'}>
    Reschedule
  </Button>
}

const isBefore = (date: string) => moment(date).isBefore(moment());
